/**
 * services/encryption.ts — EncryptionService for the UCB Dashboard.
 *
 * MUST be byte-for-byte symmetric with the API's `_common/EncryptDecrypt.php`.
 * The TNSC reference at `Web-Dashboard/src/helpers/crypto.ts` is the working
 * implementation; this file mirrors it exactly so the two stacks share the
 * same wire format.
 *
 * Wire format (hex-encoded string the PHP `decryptDataFunc` consumes):
 *
 *   [0x02 version][16-byte salt][12-byte IV][ciphertext][16-byte GCM tag]
 *
 *   key = PBKDF2-HMAC-SHA256(ENCRYPTION_MASTER_KEY || secretKey, salt, 100k, 32)
 *
 * Master key:
 *   `REACT_APP_ENCRYPTION_MASTER_KEY` is baked into the bundle. It's NOT a
 *   network secret — it's a shared PBKDF2 seed. The per-session `secretKey`
 *   (rotated on every login + refresh) is what makes the envelope safe.
 *
 * Implementation choice:
 *   We use @noble/ciphers / @noble/hashes (pure-JS) instead of WebCrypto
 *   because the call sites (axios interceptors) are synchronous. AES-GCM
 *   on <1KB payloads is sub-millisecond — premature optimisation to async.
 */

import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2 } from '@noble/hashes/pbkdf2.js';
import { sha256 } from '@noble/hashes/sha2.js';

const VERSION_BYTE = 0x02;
const SALT_LEN = 16;
const IV_LEN = 12;
const TAG_LEN = 16;
const PBKDF2_ITERS = 100000;
const AES_KEY_LEN = 32;

// ────────────────── byte / hex helpers ───────────────────────────────────

function hexToBytes(hex: string): Uint8Array {
	const clean = hex.trim();
	if (clean.length % 2 !== 0) {
		throw new Error('hexToBytes: odd-length hex string');
	}
	const out = new Uint8Array(clean.length / 2);
	for (let i = 0; i < clean.length; i += 2) {
		out[i / 2] = parseInt(clean.substr(i, 2), 16);
	}
	return out;
}

function bytesToHex(bytes: Uint8Array): string {
	let s = '';
	for (let i = 0; i < bytes.length; i += 1) {
		const b = bytes[i].toString(16);
		s += b.length === 1 ? `0${b}` : b;
	}
	return s;
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
	let total = 0;
	for (const p of parts) total += p.length;
	const out = new Uint8Array(total);
	let offset = 0;
	for (const p of parts) {
		out.set(p, offset);
		offset += p.length;
	}
	return out;
}

/** Pull the master key from the CRA env. Accepts 64 hex chars OR a raw string. */
function getMasterKeyBytes(): Uint8Array {
	const raw = process.env.REACT_APP_ENCRYPTION_MASTER_KEY || '';
	if (!raw) {
		throw new Error(
			'UCB-Dashboard-Web: REACT_APP_ENCRYPTION_MASTER_KEY is not set. ' +
				'Copy .env.example -> .env and fill the same value as the API server.',
		);
	}
	if (/^[a-f0-9]{64}$/i.test(raw)) {
		return hexToBytes(raw);
	}
	return new TextEncoder().encode(raw);
}

/** PBKDF2(master || secretKey, salt, 100k, 32) — symmetric with PHP. */
function deriveAesKey(secretKey: string, salt: Uint8Array): Uint8Array {
	const master = getMasterKeyBytes();
	const labelBytes = new TextEncoder().encode(secretKey);
	return pbkdf2(sha256, concatBytes(master, labelBytes), salt, {
		c: PBKDF2_ITERS,
		dkLen: AES_KEY_LEN,
	});
}

// ────────────────── encrypt / decrypt ────────────────────────────────────

/**
 * Encrypt any JSON-serialisable value under the session's secretKey.
 * Returns the hex-encoded envelope the PHP `decryptDataFunc` can consume.
 */
export function encryptPayload(data: unknown, secretKey: string): string {
	const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
	const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
	const dk = deriveAesKey(secretKey, salt);

	const plainJson = JSON.stringify(data);
	const plaintext = new TextEncoder().encode(plainJson);

	// @noble's gcm returns ciphertext || tag as a single Uint8Array.
	const ciphertextAndTag = gcm(dk, iv).encrypt(plaintext);

	const envelope = concatBytes(new Uint8Array([VERSION_BYTE]), salt, iv, ciphertextAndTag);
	return bytesToHex(envelope);
}

/**
 * Decrypt a value produced by `encryptPayload` or the PHP `encryptDataFunc`.
 * Returns the decoded value, or `null` on any failure — the API's decrypt
 * path is also null-on-failure so callers can check once and move on.
 */
export function decryptPayload<T = unknown>(hex: string, secretKey: string): T | null {
	if (typeof hex !== 'string' || !/^[a-f0-9]+$/i.test(hex)) {
		return null;
	}
	const bin = hexToBytes(hex);
	if (bin.length < 1 + SALT_LEN + IV_LEN + TAG_LEN + 1) {
		return null;
	}
	if (bin[0] !== VERSION_BYTE) {
		return null;
	}

	const salt = bin.slice(1, 1 + SALT_LEN);
	const iv = bin.slice(1 + SALT_LEN, 1 + SALT_LEN + IV_LEN);
	const cipherAndTag = bin.slice(1 + SALT_LEN + IV_LEN);

	try {
		const dk = deriveAesKey(secretKey, salt);
		const plaintext = gcm(dk, iv).decrypt(cipherAndTag);
		const json = new TextDecoder().decode(plaintext);
		return JSON.parse(json) as T;
	} catch {
		// Tag mismatch, bad key, tampered envelope — return null, never throw.
		return null;
	}
}

/**
 * Wrap a request body in the envelope the API expects for authenticated
 * POST/PATCH endpoints: `{ DecryptValue, Secratekey }`. The API key spelling
 * is "Secratekey" (NOT "SecretKey") to mirror the TNSC contract — do not
 * "fix" the typo.
 */
export function wrapRequest(
	data: unknown,
	secretKey: string,
): { DecryptValue: string; Secratekey: string } {
	return {
		DecryptValue: encryptPayload(data, secretKey),
		Secratekey: secretKey,
	};
}

/**
 * Static singleton wrapper so callers can inject `EncryptionService` in
 * tests. The actual implementation is the module-scoped functions above.
 */
export const EncryptionService = {
	encryptPayload,
	decryptPayload,
	wrapRequest,
};
